// frontend/modules/template/components/ImportExportScripts.tsx
import React, { useState } from 'react';
import { 
    Button, 
    Modal, 
    Upload, 
    message, 
    Space, 
    Typography, 
    Alert
} from 'antd';
import { 
    UploadOutlined, 
    DownloadOutlined, 
    InboxOutlined 
} from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';
import { useContextScripts } from '../hooks/useContextScripts';
import { ContextScriptImportResult } from '../types/context-script.types';

const { Dragger } = Upload;
const { Text, Title } = Typography;

interface ImportExportScriptsProps {
    visible: boolean;
    onClose: () => void;
}

export const ImportExportScripts: React.FC<ImportExportScriptsProps> = ({
    visible,
    onClose
}) => {
    const [importMode, setImportMode] = useState(true);
    const [importLoading, setImportLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [importResult, setImportResult] = useState<ContextScriptImportResult | null>(null);
    
    const { importScripts, exportScripts } = useContextScripts();
    
    const handleImport = async (file: RcFile) => {
        try {
            setImportLoading(true);
            setImportResult(null);
            
            // Read the file
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const content = e.target?.result as string;
                    const importData = JSON.parse(content);
                    // Validate import data structure
                    if (!importData.scripts) {
                        throw new Error('Invalid import file format');
                    }
                    
                    // Send to API
                    const result = await importScripts(importData);

                    // @ts-ignore
                    setImportResult(result);

                    // @ts-ignore
                    if (result.importedCount > 0) {
                        // @ts-ignore
                        message.success(`Successfully imported ${result.importedCount} scripts`);
                    } else {
                        message.warning('No scripts were imported');
                    }
                } catch (error) {
                    console.error('Import error:', error);
                    message.error('Failed to import scripts: Invalid file format');
                } finally {
                    setImportLoading(false);
                }
            };
            
            reader.readAsText(file);
            
            // Prevent default upload behavior
            return false;
        } catch (error) {
            console.error('Import error:', error);
            message.error('Failed to import scripts');
            setImportLoading(false);
            return false;
        }
    };
    
    const handleExport = async () => {
        try {
            setExportLoading(true);
            
            const exportData = await exportScripts();
            
            // Create a download link
            const dataStr = JSON.stringify(exportData, null, 2);
            const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
            
            const fileName = `context-scripts-${new Date().toISOString().slice(0, 10)}.json`;
            
            const linkElement = document.createElement('a');
            linkElement.setAttribute('href', dataUri);
            linkElement.setAttribute('download', fileName);
            linkElement.click();
            
            message.success('Scripts exported successfully');
            setExportLoading(false);
        } catch (error) {
            console.error('Export error:', error);
            message.error('Failed to export scripts');
            setExportLoading(false);
        }
    };
    
    const switchMode = (mode: 'import' | 'export') => {
        setImportMode(mode === 'import');
        setImportResult(null);
    };
    
    return (
        <Modal
            title={importMode ? 'Import Context Scripts' : 'Export Context Scripts'}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={600}
        >
            <div className="mb-4">
                <Space>
                    <Button
                        type={importMode ? 'primary' : 'default'}
                        onClick={() => switchMode('import')}
                    >
                        Import
                    </Button>
                    <Button
                        type={!importMode ? 'primary' : 'default'}
                        onClick={() => switchMode('export')}
                    >
                        Export
                    </Button>
                </Space>
            </div>
            
            {importMode ? (
                <>
                    <Dragger
                        name="file"
                        multiple={false}
                        accept=".json"
                        beforeUpload={handleImport}
                        showUploadList={false}
                        disabled={importLoading}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                            Click or drag context scripts JSON file to this area to upload
                        </p>
                        <p className="ant-upload-hint">
                            Only .json files exported from this system are supported.
                        </p>
                    </Dragger>
                    
                    {importLoading && (
                        <div className="mt-4 text-center">
                            <Text>Importing scripts...</Text>
                        </div>
                    )}
                    
                    {importResult && (
                        <div className="mt-4">
                            <Title level={5}>Import Results</Title>
                            <ul>
                                <li>Imported: {importResult.importedCount}</li>
                                <li>Skipped: {importResult.skippedCount}</li>
                                {/* @ts-ignore */}
                                <li>Warnings: {importResult.warningCount}</li>
                            </ul>
                            
                            {importResult.errors && importResult.errors.length > 0 && (
                                <Alert
                                    message="Errors"
                                    description={(
                                        <ul>
                                            {importResult.errors.map((error, index) => (
                                                <li key={index}>{error}</li>
                                            ))}
                                        </ul>
                                    )}
                                    type="error"
                                    showIcon
                                />
                            )}
                            
                            {importResult.warnings && importResult.warnings.length > 0 && (
                                <Alert
                                    message="Warnings"
                                    description={(
                                        <ul>
                                            {importResult.warnings.map((warning, index) => (
                                                <li key={index}>{warning}</li>
                                            ))}
                                        </ul>
                                    )}
                                    type="warning"
                                    showIcon
                                    className="mt-4"
                                />
                            )}
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className="text-center">
                        <Button 
                            type="primary" 
                            icon={<DownloadOutlined />} 
                            onClick={handleExport}
                            loading={exportLoading}
                        >
                            Export Scripts
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};
